import { ReactNode, useEffect, useRef, useState } from 'react'
import { PageHeader } from 'components/page-header'
import { MonitoringBadge } from 'components/monitoring-badge'
import { TabItem } from 'components/tabs/types'

interface PageLayoutProps {
	title: string
	subtitle?: ReactNode
	tags?: Array<{ icon?: any; label?: string | null }>
	tagline?: string
	titleBadge?: ReactNode
	breadcrumbs?: ReactNode
	datePicker?: ReactNode
	topAction?: ReactNode
	notices?: JSX.Element
	tabs?: Array<TabItem>
	currentTab?: string
	tabsActions?: any
	children: ReactNode
}

export const PageLayout = (props: PageLayoutProps) => {
	const {
		title,
		subtitle,
		tags,
		tagline,
		titleBadge,
		breadcrumbs,
		datePicker,
		topAction,
		notices,
		tabs,
		currentTab,
		tabsActions,
		children
	} = props

	const sentinelRef = useRef<HTMLDivElement>(null)
	const [isStuck, setIsStuck] = useState(false)

	useEffect(() => {
		const sentinel = sentinelRef.current
		if (!sentinel) return
		const observer = new IntersectionObserver(
			([entry]) => setIsStuck(!entry.isIntersecting),
			{ threshold: 0 }
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [])

	return (
		<div>
			<div ref={sentinelRef} aria-hidden="true" className="h-px -mb-px" />
			<div
				className={`app-page-surface sticky top-0 z-[25] bg-[#fafafa] border-b transition-colors ${
					isStuck ? 'border-[rgb(var(--cg-border))]' : 'border-transparent'
				}`}
			>
				{/* Wraps instead of squeezing: once the title block would drop below
				    16rem, the date picker moves to its own line under it. */}
				<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-4 pb-3">
					<div className="flex-1 min-w-[min(100%,16rem)]">
						<PageHeader
							noPadding={false}
							title={title}
							subtitle={subtitle}
							tags={tags}
							tagline={tagline}
							titleBadge={titleBadge ?? <MonitoringBadge />}
							actionPlacement="top"
							action={topAction ? <>{topAction}</> : undefined}
							tabs={tabs}
							currentTab={currentTab}
							tabsActions={tabsActions}
						/>
					</div>
					{datePicker && <div className="flex-shrink-0 px-4">{datePicker}</div>}
				</div>
			</div>
			{notices && <div>{notices}</div>}
			{breadcrumbs && (
				<div className="w-full flex items-center px-4 py-3">{breadcrumbs}</div>
			)}
			<div className="app-page-surface px-4 pt-6 pb-4 rounded-b-[10px] bg-[#fafafa]">
				{children}
			</div>
		</div>
	)
}
